// file: src/LandlordPages/LandlordResetPassword.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { confirmPasswordReset } from "../api/landlordApi";

const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

/* Icons (no deps) */
const Eye = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
);
const EyeOff = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M3 3l18 18M2 12s3.5-7 10-7c1.7 0 3.2.4 4.6 1.1M22 12s-3.5 7-10 7c-1.7 0-3.2-.4-4.6-1.1" />
  </svg>
);

/* Validation */
const Schema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Min 8 characters")
    .required("Password is required"),
  confirm: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
});

/* Small password strength helper */
function strengthLabel(pw = "") {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 2) return { label: "Weak", value: 0.34 };
  if (score === 3) return { label: "Okay", value: 0.6 };
  return { label: "Strong", value: 1 };
}

/* Reusable Password field with show/hide */
function PasswordField({ id, name, placeholder, value, onChange, onBlur }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Field
        id={id}
        name={name}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 pr-12 outline-none focus:ring-4"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute inset-y-0 right-2 grid place-items-center rounded-lg px-2 text-slate-600 hover:text-slate-900"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
}

export default function LandlordResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const token = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get("token") || "";
  }, [location.search]);

  const emailFromLink = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get("email") || "";
  }, [location.search]);

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          Invalid or missing token. Please re-request a password reset.
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-white text-slate-900">
      {/* Soft ambient accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: `${BLUE}14` }}
        />
        <div
          className="absolute -bottom-24 right-8 h-72 w-72 rounded-full blur-3xl"
          style={{ background: `${BLUE_LIGHT}12` }}
        />
      </div>

      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-3xl border border-black/[0.06] bg-white/90 p-6 shadow-xl backdrop-blur">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div
              className="grid h-11 w-11 place-items-center rounded-2xl text-white shadow-lg"
              style={{
                background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
                boxShadow: `0 12px 28px ${BLUE}33`,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
                <path
                  d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-semibold leading-6 tracking-tight">
                Reset your password
              </h1>
              <p className="text-xs text-slate-500">
                Enter your email and new password.
              </p>
            </div>
          </div>

          {/* Success state */}
          {done ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                Your password has been reset successfully. You can now sign in
                with your new password.
              </div>
              <button
                onClick={() => navigate("/landlord/signin", { replace: true })}
                className="w-full rounded-xl bg-[color:var(--b)] px-4 py-3 font-semibold text-white shadow-lg"
                style={{ ["--b"]: BLUE, boxShadow: `0 12px 26px ${BLUE}33` }}
              >
                Go to sign in
              </button>
            </div>
          ) : (
            <Formik
              initialValues={{
                email: emailFromLink,
                password: "",
                confirm: "",
              }}
              validationSchema={Schema}
              onSubmit={async (values, { setSubmitting }) => {
                setError("");
                try {
                  await confirmPasswordReset({
                    email: values.email.trim(),
                    token,
                    newPassword: values.password,
                  });
                  setDone(true);
                } catch (e) {
                  setError(e?.message || "Failed to reset password.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, values, handleChange, handleBlur }) => {
                const s = strengthLabel(values.password);
                return (
                  <Form className="mt-6 space-y-4">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium">Email</label>
                      <Field
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 outline-none focus:ring-4"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>

                    {/* New password + strength */}
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium">
                          New password
                        </label>
                        {values.password && (
                          <div
                            className={`text-xs font-medium ${
                              s.label === "Strong"
                                ? "text-emerald-600"
                                : s.label === "Okay"
                                ? "text-amber-600"
                                : "text-rose-600"
                            }`}
                          >
                            {s.label}
                          </div>
                        )}
                      </div>

                      <PasswordField
                        id="password"
                        name="password"
                        placeholder="••••••••"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {/* Strength bar */}
                      <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200">
                        <div
                          className="h-full rounded-full transition-[width]"
                          style={{
                            width: `${Math.max(8, Math.floor(s.value * 100))}%`,
                            background:
                              s.label === "Strong"
                                ? "linear-gradient(90deg,#10B981,#34D399)"
                                : s.label === "Okay"
                                ? "linear-gradient(90deg,#F59E0B,#FBBF24)"
                                : "linear-gradient(90deg,#EF4444,#F87171)",
                          }}
                        />
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>

                    {/* Confirm */}
                    <div>
                      <label className="block text-sm font-medium">
                        Confirm password
                      </label>
                      <PasswordField
                        id="confirm"
                        name="confirm"
                        placeholder="••••••••"
                        value={values.confirm}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage
                        name="confirm"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {error}
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-[color:var(--b)] px-4 py-3 font-semibold text-white shadow-lg transition active:scale-[0.99] disabled:opacity-60"
                      style={{
                        ["--b"]: BLUE,
                        boxShadow: `0 12px 26px ${BLUE}33`,
                      }}
                    >
                      <span className="absolute inset-0 -translate-x-full bg-white/15 transition-transform duration-700 group-hover:translate-x-full" />
                      {isSubmitting ? "Resetting…" : "Reset password"}
                    </button>

                    {/* Helper text */}
                    <p className="text-center text-xs text-slate-500">
                      Make it strong: at least 8 characters with
                      upper/lowercase, a number and a symbol.
                    </p>
                  </Form>
                );
              }}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
}
